import { type ReactNode, useState } from 'react'
import { settingsCopy } from '@/copy'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { Select } from '@/ui/Field/Field'
import { Inline } from '@/ui/layout/Inline'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { MEMBER_ROLES } from '../hooks/useWorkspaceMembers'
import type { WorkspaceMemberInfo } from '../types/api'
import styles from './MemberList.module.css'

interface Props {
  members: WorkspaceMemberInfo[]
  canManage: boolean
  onUpdateRole: (userId: string, role: string) => void
  onRemove: (userId: string) => void
  isUpdating: boolean
  isRemoving: boolean
  currentUserId?: string | null
  canTransferOwnership?: boolean
  onTransferOwnership?: (userId: string) => void
  isTransferring?: boolean
  /** A last row after the members, for example the "Add member" action. */
  footer?: ReactNode
}

/**
 * Member row list (role select + remove) shared by the admin workspace card
 * and workspace-settings members tab. Owns no data fetching/mutations, and
 * intentionally excludes the "add member" form — the admin and settings call
 * sites add members through different flows (known-user dropdown vs. free-text
 * username) that aren't safe to conflate here.
 */
export function MemberList({
  members,
  canManage,
  onUpdateRole,
  onRemove,
  isUpdating,
  isRemoving,
  currentUserId,
  canTransferOwnership = false,
  onTransferOwnership,
  isTransferring = false,
  footer,
}: Props) {
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null)
  const [confirmTransferId, setConfirmTransferId] = useState<string | null>(
    null,
  )
  const memberToRemove = members.find((m) => m.user_id === confirmRemoveId)
  const memberToReceiveOwnership = members.find(
    (member) => member.user_id === confirmTransferId,
  )
  const canTransfer = (member: WorkspaceMemberInfo) =>
    canTransferOwnership &&
    onTransferOwnership !== undefined &&
    member.user_id !== currentUserId &&
    member.role !== 'owner'

  return (
    <>
      <RowList aria-label={settingsCopy.members.listLabel}>
        {members.map((member, index) => (
          <RowListItem key={member.user_id}>
            <Avatar
              name={member.username}
              kind="human"
              size="md"
              alt={index % 2 === 1}
            />
            <Text tone="text-sub" className={styles.name}>
              {member.username}
            </Text>
            {canManage ? (
              <span className={styles.role}>
                <Select
                  aria-label={settingsCopy.members.roleFor(member.username)}
                  value={member.role}
                  onChange={(e) => onUpdateRole(member.user_id, e.target.value)}
                  disabled={isUpdating || member.user_id === currentUserId}
                >
                  {MEMBER_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </span>
            ) : (
              <Text tone="text-mute">{member.role}</Text>
            )}
            {(canManage || canTransfer(member)) && (
              <Inline gap="space-8" wrap={false}>
                {canManage && (
                  <Button
                    variant="ghost"
                    disabled={member.user_id === currentUserId}
                    onClick={() => setConfirmRemoveId(member.user_id)}
                  >
                    {settingsCopy.members.remove}
                  </Button>
                )}
                {canTransfer(member) && (
                  <Button
                    variant="ghost"
                    disabled={isTransferring}
                    onClick={() => setConfirmTransferId(member.user_id)}
                  >
                    {settingsCopy.members.transfer}
                  </Button>
                )}
              </Inline>
            )}
          </RowListItem>
        ))}
        {footer ? <RowListItem>{footer}</RowListItem> : null}
      </RowList>

      <ConfirmDialog
        open={confirmRemoveId !== null}
        title={settingsCopy.members.removeDialog.title}
        tone="danger"
        description={
          memberToRemove
            ? settingsCopy.members.removeDialog.description(
                memberToRemove.username,
              )
            : undefined
        }
        confirmLabel={settingsCopy.members.removeDialog.confirm}
        loading={isRemoving}
        onConfirm={() => {
          if (confirmRemoveId) onRemove(confirmRemoveId)
          setConfirmRemoveId(null)
        }}
        onClose={() => setConfirmRemoveId(null)}
      />

      <ConfirmDialog
        open={confirmTransferId !== null}
        title={settingsCopy.members.transferDialog.title}
        tone="default"
        description={
          memberToReceiveOwnership
            ? settingsCopy.members.transferDialog.description(
                memberToReceiveOwnership.username,
              )
            : undefined
        }
        confirmLabel={settingsCopy.members.transferDialog.confirm}
        loading={isTransferring}
        onConfirm={() => {
          if (confirmTransferId && onTransferOwnership) {
            onTransferOwnership(confirmTransferId)
          }
          setConfirmTransferId(null)
        }}
        onClose={() => setConfirmTransferId(null)}
      />
    </>
  )
}
